import { useCallback, useEffect, useMemo, useState } from 'react';

import { BLOCKED, ALLOWED } from '@helpcenter/forge-ui-utils';
import type { AggExtension, ExtensionPointModule } from '@helpcenter/forge-ui-utils';
import { getCloudId } from '@helpcenter/meta';
import { di } from 'react-magnetic-di';
import { fetchQuery, graphql } from 'react-relay';
import type { LicenseDetails } from '@atlassian/forge-ui-types';
import environment from '@atlassian/help-center-common-util/relay/environment';

import type { useExtensionsQuery, useExtensionsQuery$data } from './__generated__/useExtensionsQuery.graphql';
import type { AnalyticsAttributes } from 'view/forge-ui-analytics/common/types';

interface PortalRequestContext {
    portalId: string;
    requestTypeId: string;
}

export type SupportedContext = PortalRequestContext /* | AnotherContext */;

/* eslint-disable relay/unused-fields */
export const FORGE_FIELD_QUERY = graphql`
    query useExtensionsQuery(
        $cloudId: ID!
        $context: JiraExtensionRenderingContextInput!
        $types: [String!]!
        $includeHidden: Boolean!
    ) {
        jira {
            forge {
                extensions(cloudId: $cloudId, context: $context, types: $types, includeHidden: $includeHidden) {
                    hiddenBy
                    type
                    id
                    moduleId
                    environmentId
                    environmentKey
                    environmentType
                    installationId
                    appVersion
                    consentUrl
                    properties
                    userAccess {
                        hasAccess
                        enabled
                    }
                    egress {
                        addresses
                        type
                        category
                        inScopeEUD
                    }
                    installationConfig {
                        key
                        value
                    }
                    license {
                        active
                        type
                        supportEntitlementNumber
                        trialEndDate
                        subscriptionEndDate
                        isEvaluation
                        billingPeriod
                        ccpEntitlementId
                        ccpEntitlementSlug
                        capabilitySet
                    }
                }
            }
        }
    }
`;
/* eslint-enable relay/unused-fields */

export const useExtensions = <T extends AggExtension>({
    types,
    context,
    includeHidden,
    shouldFetch,
    onInitializationFinished,
    onInitializationFailed,
}: {
    types: ExtensionPointModule[];
    context: SupportedContext;
    includeHidden: boolean;
    shouldFetch: boolean;
    onInitializationFinished: (attributes?: AnalyticsAttributes) => Promise<void>;
    onInitializationFailed: (attributes?: AnalyticsAttributes) => Promise<void>;
}) => {
    di(fetchQuery, getCloudId);

    const cloudId = getCloudId();
    const [isLoading, setIsLoading] = useState(shouldFetch);
    const [extensions, setExtensions] = useState<T[]>([]);
    const [error, setError] = useState<Error | null>(null);

    const fetchForgeModules = useCallback(() => {
        setIsLoading(true);
        fetchQuery<useExtensionsQuery>(environment, FORGE_FIELD_QUERY, {
            cloudId,
            context,
            types,
            includeHidden,
        })
            .toPromise()
            .then((data: useExtensionsQuery$data) => {
                const exts = (data?.jira?.forge?.extensions || [])
                    .filter((ext) => types.includes(ext.type as ExtensionPointModule))
                    .map(transformExtension<T>);
                setExtensions(exts);
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                onInitializationFinished();
            })
            .catch((e: Error) => {
                setExtensions([]);
                setError(e);
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                onInitializationFailed({ error: e });
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [cloudId, context, includeHidden, types, onInitializationFinished, onInitializationFailed]);

    useEffect(() => {
        if (shouldFetch) {
            fetchForgeModules();
        }
    }, [shouldFetch, fetchForgeModules]);

    return useMemo(() => ({ extensions, isLoading, error }), [error, extensions, isLoading]);
};

/**
 * General notes:
 * Some types returned by the new API are not the same as the ones in Forge UI types.
 * This transformer fixes minor mismatches between the types e.g. null <> undefined conversions.
 * Additionally, this transformer makes new API types compatible with the old ones to avoid type errors and breaking changes.
 *
 * License notes:
 * It doesn't transform `undefined` to `boolean` values since it isn't a right thing to do.
 * It does transform `undefined` to `null` though.
 */
export const transformExtension = <T extends AggExtension>(
    extension: NonNullable<NonNullable<NonNullable<useExtensionsQuery$data['jira']>['forge']>['extensions']>[number]
) => {
    const license = extension.license;
    const transformedLicense = license
        ? {
              active: license.active,
              type: (license.type ?? null) as LicenseDetails['type'],
              supportEntitlementNumber: license.supportEntitlementNumber ?? null,
              trialEndDate: (license.trialEndDate ?? null) as LicenseDetails['trialEndDate'],
              subscriptionEndDate: (license.subscriptionEndDate ?? null) as LicenseDetails['subscriptionEndDate'],
              isEvaluation: (license.isEvaluation ?? null) as LicenseDetails['isEvaluation'],
              billingPeriod: (license.billingPeriod ?? null) as LicenseDetails['billingPeriod'],
              ccpEntitlementId: (license.ccpEntitlementId ?? null) as LicenseDetails['ccpEntitlementId'],
              ccpEntitlementSlug: (license.ccpEntitlementSlug ?? null) as LicenseDetails['ccpEntitlementSlug'],
              capabilitySet: (license.capabilitySet ?? null) as LicenseDetails['capabilitySet'],
              modes: null,
          }
        : null;

    // Each Forge module has a unique id, however it may be used in different contexts.
    // In those contexts, some modules may be hidden depending on display conditions.
    // In order for Relay to not pick up hidden extensions from the cache, we need to use a unique id for each context.
    // This is done on the backend side by appending the hash to the module id.
    // This fixes the Relay cache issue but on the client side we still want to operate on the original extension id since Forge depends on it.
    // That's why we swap id with moduleId, which represents the original id; also as a fallback we strip the hash from the id just in case.
    const id = (extension.moduleId as string) ?? extension.id.replace(/-+\d+$/, '');

    return {
        ...extension,
        id,
        key: id,
        egress: extension.egress ?? [],
        consentUrl: extension.consentUrl ?? undefined,
        license: transformedLicense,
        dataClassificationPolicyDecision: {
            status: extension.hiddenBy === 'AppAccessRules' ? BLOCKED : ALLOWED,
        },
    } as T;
};
